'use client';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

interface DataPoint {
  name: string;
  [key: string]: string | number;
}

interface MetricBarChartProps {
  data: DataPoint[];
  bars: Array<{
    dataKey: string;
    name: string;
    color?: string;
  }>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  targetLine?: number;
  height?: number;
  stacked?: boolean;
}

const BRAND_COLORS = {
  navy: '#0A1E2E',
  orange: '#E89841',
  cream: '#E8D5B7',
  blue: '#4A90E2',
  green: '#6FCF97',
  red: '#EB5757',
};

export default function MetricBarChart({
  data,
  bars,
  xAxisLabel,
  yAxisLabel,
  targetLine,
  height = 300,
  stacked = false,
}: MetricBarChartProps) {
  const defaultColors = [BRAND_COLORS.orange, BRAND_COLORS.blue, BRAND_COLORS.green];

  return (
    <div className="w-full my-6 p-4 bg-background/40 border border-border rounded-lg">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={BRAND_COLORS.cream + '20'} />
          <XAxis
            dataKey="name"
            stroke={BRAND_COLORS.cream}
            tick={{ fill: BRAND_COLORS.cream }}
            label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5, fill: BRAND_COLORS.cream } : undefined}
          />
          <YAxis
            stroke={BRAND_COLORS.cream}
            tick={{ fill: BRAND_COLORS.cream }}
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fill: BRAND_COLORS.cream } : undefined}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: BRAND_COLORS.navy,
              border: `1px solid ${BRAND_COLORS.orange}40`,
              borderRadius: '8px',
              color: BRAND_COLORS.cream,
            }}
            labelStyle={{ color: BRAND_COLORS.orange }}
          />
          <Legend
            wrapperStyle={{ color: BRAND_COLORS.cream }}
          />
          {targetLine !== undefined && (
            <ReferenceLine
              y={targetLine}
              stroke={BRAND_COLORS.green}
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{ value: 'Target', fill: BRAND_COLORS.green, position: 'right' }}
            />
          )}
          {bars.map((bar, index) => (
            <Bar
              key={bar.dataKey}
              dataKey={bar.dataKey}
              name={bar.name}
              fill={bar.color || defaultColors[index % defaultColors.length]}
              stackId={stacked ? 'stack' : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
